import { useMemo } from 'react';
import type { ReactNode } from 'react';

import type {
  DtoV1Borrower,
  DtoV1RealEstateOwnedRealEstateOwnedSummaryRecord,
} from '~/__generated__/api';
import Button from '~/common/components/Button';
import CrudGrid from '~/common/components/CrudGrid';
import { OPERATORS } from '~/common/components/CrudGrid/Filters/filterGridData';
import type { GridDef } from '~/common/components/CrudGrid/GridDef';
import { renderBoolean } from '~/common/components/CrudGrid/crudGridUtils';
import { formatCurrency } from '~/common/util/format';

import { formatName } from '../../common/utils/utils';
import type { RowToEdit } from '../Form/RealEstateOwnedSummariesForm';

type AddressId = {
  addressId: number;
  addressLdId: number;
};

type PropertyAddress = {
  wholeAddressString?: string;
  addressType?: string;
};

type GridDefRow = {
  propertyAddress?: PropertyAddress;
};

type ColumnOption = {
  label: string | number;
  value: string | number;
};

type ColumnOptions = {
  availablePropertyStatusTypes?: ColumnOption[];
  availablePropertyTypes?: ColumnOption[];
  [key: string]: ColumnOption[] | undefined;
};

type Column = {
  fieldName: string;
  headerName: string;
  isNumeric?: boolean;
  useOptions?: boolean;
  renderCell?: (val: unknown, options?: ColumnOptions) => ReactNode;
};

type FilterOption = {
  value: number;
  label: string;
};

type Filter = {
  options: FilterOption[] | undefined;
  label: string;
  fieldName: string;
  operator: number;
  evaluateFilter: (row: RowToEdit, value: number[]) => boolean;
};

export type RowToDelete = {
  addressId: AddressId;
};

type RealEstateOwnedCrudGridData = {
  realEstateOwned?: DtoV1RealEstateOwnedRealEstateOwnedSummaryRecord[] | null;
  dropDownOptions?: ColumnOptions;
  availableBorrowers?: DtoV1Borrower[] | null;
};

type RealEstateOwnedCrudGridProps = {
  data: RealEstateOwnedCrudGridData | undefined;
  isLoading: boolean;
  rowToEdit: RowToEdit | null;
  isWriteAccessDisabled: boolean;
  isREO1003AndPropertyEnabled: boolean;
  onEditMode: (row: RowToEdit, isEdit: boolean) => void;
  onAddMode: () => void;
  onDelete: (row: RowToDelete[]) => void;
};

const gridDef: GridDef<GridDefRow> = {
  uniqueIdAttributeFields: ['addressId.addressId'],
  allowFilters: true,
  gridLanguage: {
    singularItem: 'REO',
    pluralItem: 'REOs',
  },
  getDialogDeleteMessage: (row) =>
    `Are you sure you want to remove this REO: ${
      row[0].propertyAddress?.wholeAddressString
    }${
      row[0].propertyAddress?.addressType
        ? ` - ${row[0].propertyAddress.addressType}`
        : ''
    }?`,
};

const columns: Column[] = [
  {
    fieldName: 'wholeAddressString',
    headerName: 'Property Address',
  },
  {
    fieldName: 'propertyStatus',
    headerName: 'Property Status',
    useOptions: true,
    renderCell: (val, options: ColumnOptions) => {
      return options.availablePropertyStatusTypes?.find(
        ({ value }) => value === val,
      )?.label;
    },
  },
  {
    fieldName: 'propertyTypeId',
    headerName: 'Property Type',
    useOptions: true,
    renderCell: (val, options: ColumnOptions) =>
      options.availablePropertyTypes?.find(({ value }) => value === val)?.label,
  },
  {
    fieldName: 'isSubjectProperty',
    headerName: 'Subject Property',
    renderCell: (val) => renderBoolean(val as boolean),
  },
  {
    fieldName: 'marketValue',
    headerName: 'Market Value',
    isNumeric: true,
    renderCell: (val) => formatCurrency(val),
  },
  {
    fieldName: 'mortgagePayment',
    headerName: 'Mortgage Payment',
    isNumeric: true,
    renderCell: (val) => formatCurrency(val),
  },
  {
    fieldName: 'netRentalIncome',
    headerName: 'Net Rent',
    isNumeric: true,
    renderCell: (val) => formatCurrency(val),
  },
];

export function RealEstateOwnedCrudGrid({
  data,
  isLoading,
  rowToEdit,
  isWriteAccessDisabled,
  isREO1003AndPropertyEnabled,
  onEditMode,
  onAddMode,
  onDelete,
}: Readonly<RealEstateOwnedCrudGridProps>) {
  const filters: Filter[] = [
    {
      options: data?.availableBorrowers?.map((borrower): FilterOption => {
        return {
          value: borrower.customerRecordId ?? 0,
          label: formatName(
            borrower.firstName,
            borrower.lastName,
            borrower.middleName,
            borrower.suffixName,
          ),
        };
      }),
      label: 'Borrower(s)',
      fieldName: 'associatedBorrowers',
      operator: OPERATORS.INCLUDE,
      evaluateFilter: (row, value) =>
        row.associatedBorrowers?.some((borrower) =>
          value.includes(borrower.customerRecordId),
        ) ?? false,
    },
  ];

  const mappedRealEstateOwned = useMemo(() => {
    return Array.isArray(data?.realEstateOwned)
      ? data.realEstateOwned.map((reo) => {
          return {
            ...reo,
            ...reo.propertyAddress,
            ...reo.propertyType,
            ...reo.associatedLiabilities,
          };
        })
      : [];
  }, [data?.realEstateOwned]);

  return (
    <CrudGrid
      gridDef={gridDef}
      filters={filters}
      columns={columns}
      data={mappedRealEstateOwned}
      dropdownOptions={data?.dropDownOptions}
      onRowEditClick={(row) => {
        onEditMode(row, true);
      }}
      readOnly={isWriteAccessDisabled}
      selectedRow={rowToEdit}
      isLoading={isLoading}
      disableRowClick={!isREO1003AndPropertyEnabled}
      onDelete={onDelete}
      actions={
        <Button
          dataTestid="add-reo-button"
          isDisabled={!isREO1003AndPropertyEnabled}
          onClick={() => {
            onAddMode();
          }}
        >
          Add REO
        </Button>
      }
    />
  );
}
